"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Loader, Calendar, MapPin, User } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
export default function BookYourLuxuryExperience() {
  return <section id="book-your-luxury-experience" className="bg-background py-24 lg:py-32 px-6 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-16 lg:gap-24 items-center">
        {}
        <div className="w-full lg:w-[40%] flex flex-col justify-center">
          <h2 className="font-default text-secondary text-5xl md:text-6xl lg:text-7xl leading-[1.1] mb-8">
            Begin Your Odyssey
          </h2>
          <div className="space-y-6 font-sans text-foreground/80 text-lg leading-relaxed">
            <p>
              Embark on a journey tailored exclusively to your desires. Our
              curated experiences transcend the ordinary, offering unparalleled
              access to the world's most breathtaking destinations.
            </p>
            <p>
              Share your vision with our dedicated concierges, and allow us to
              craft an itinerary that anticipates your every need and exceeds
              your wildest expectations.
            </p>
          </div>
        </div>

        {}
        <div className="w-full lg:w-[60%]">
          <div className="bg-foreground p-8 md:p-12 lg:p-16 rounded-xl shadow-2xl">
            <FormProvider formId="luxury-booking-form" sectionName="Book Your Luxury Experience" defaultValues={{
            name: "",
            email: "",
            destination: "",
            date: "",
            guests: ""
          }} submitText="Request Itinerary" successMessage="Your request has been received. A concierge will contact you shortly.">
              <BookingForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function BookingForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in duration-500">
        <div className="size-16 rounded-full bg-secondary/20 flex items-center justify-center mb-4">
          <div className="size-8 rounded-full bg-secondary flex items-center justify-center">
            <svg className="size-5 text-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
        <h3 className="font-default text-3xl text-background">
          Journey Initiated
        </h3>
        <p className="text-background/80 font-sans text-lg max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-8 border-secondary text-secondary hover:bg-secondary hover:text-foreground rounded-sm px-8">
          Plan Another Journey
        </Button>
      </div>;
  }
  const inputClasses = "bg-transparent border-0 border-b border-secondary rounded-none px-0 text-background placeholder:text-background/40 focus-visible:ring-0 focus-visible:border-secondary focus-visible:border-b-2 shadow-none h-12 text-lg font-sans transition-all";
  const labelClasses = "text-background/60 text-xs uppercase tracking-widest font-sans mb-1";
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <FormField control={control} name="name" rules={{
          required: "Please provide your name"
        }} render={({
          field
        }) => <FormItem className="space-y-1">
                <FormLabel className={labelClasses}>Full Name</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input placeholder="Jane Doe" className={inputClasses} {...field} />
                    <User className="absolute right-0 top-1/2 -translate-y-1/2 size-5 text-secondary/50 pointer-events-none" />
                  </div>
                </FormControl>
                <FormMessage className="text-destructive-foreground/90 text-xs" />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address"
          }
        }} render={({
          field
        }) => <FormItem className="space-y-1">
                <FormLabel className={labelClasses}>Email Address</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="jane@example.com" className={inputClasses} {...field} />
                </FormControl>
                <FormMessage className="text-destructive-foreground/90 text-xs" />
              </FormItem>} />
        </div>

        <FormField control={control} name="destination" rules={{
        required: "Please select a destination"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelClasses}>Desired Destination</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className={`${inputClasses} flex items-center justify-between w-full data-[placeholder]:text-background/40`}>
                    <SelectValue placeholder="Select a region" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-foreground border-secondary/20 text-background">
                  <SelectItem value="mediterranean">
                    The Mediterranean
                  </SelectItem>
                  <SelectItem value="caribbean">The Caribbean</SelectItem>
                  <SelectItem value="southeast-asia">Southeast Asia</SelectItem>
                  <SelectItem value="polynesia">French Polynesia</SelectItem>
                  <SelectItem value="undecided">
                    Inspire Me (Undecided)
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage className="text-destructive-foreground/90 text-xs" />
            </FormItem>} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <FormField control={control} name="date" render={({
          field
        }) => <FormItem className="space-y-1">
                <FormLabel className={labelClasses}>
                  Anticipated Travel Date
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input type="text" placeholder="e.g., Summer 2025" className={inputClasses} {...field} />
                    <SafeHydrate><Calendar className="absolute right-0 top-1/2 -translate-y-1/2 size-5 text-secondary/50 pointer-events-none" /></SafeHydrate>
                  </div>
                </FormControl>
                <FormMessage className="text-destructive-foreground/90 text-xs" />
              </FormItem>} />

          <FormField control={control} name="guests" render={({
          field
        }) => <FormItem className="space-y-1">
                <FormLabel className={labelClasses}>Number of Guests</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className={`${inputClasses} flex items-center justify-between w-full data-[placeholder]:text-background/40`}>
                      <SelectValue placeholder="Select party size" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="bg-foreground border-secondary/20 text-background">
                    <SelectItem value="1-2">1-2 Guests</SelectItem>
                    <SelectItem value="3-4">3-4 Guests</SelectItem>
                    <SelectItem value="5-8">5-8 Guests</SelectItem>
                    <SelectItem value="9+">9+ Guests</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage className="text-destructive-foreground/90 text-xs" />
              </FormItem>} />
        </div>

        {submitError && <div className="p-4 bg-destructive/20 border border-destructive/50 rounded-sm">
            <p className="text-background text-sm">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground rounded-sm h-16 text-lg tracking-widest uppercase font-sans font-medium hover:bg-primary/90 mt-8 transition-all duration-300">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-3 size-5" />
              Processing Request...
            </> : "Submit Inquiry"}
        </Button>
      </div>
    </Form>;
}